import { Button, Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectSeparator, SelectTrigger, SelectValue } from '@1trading/react';
import { useId, useState } from 'react';
import { ContextTooltip } from '@/components/ui/context-tooltip';
import { FieldError } from '@/components/ui/field-error';
import { useQuoteSources } from '@/hooks/queries/mm-programs/use-mm-programs';
import { TextField } from './mm-program-fields';

const ENTER_MANUALLY = 'enter-manually';
const sourceKey = (provider: string, symbol: string) => `${provider}\u0000${symbol}`;

interface IndexSourcePickerProps {
  /** The exchange instrument being configured; its own feeds are offered first */
  instrument: string;
  label: string;
  description: string;
  provider: string | undefined;
  symbol: string | undefined;
  error?: string;
  onChange: (source: { provider: string; symbol: string }) => void;
}

export const IndexSourcePicker = ({ instrument, label, description, provider = '', symbol = '', error, onChange }: IndexSourcePickerProps) => {
  const pickerId = useId();
  const query = useQuoteSources();
  const sources = query.data?.sources ?? [];
  const current = provider || symbol ? sourceKey(provider, symbol) : '';
  const isListed = sources.some((source) => sourceKey(source.provider, source.symbol) === current);
  const [isManual, setIsManual] = useState(query.isError);
  const ownFeeds = sources.filter((source) => source.instruments.includes(instrument));
  const otherFeeds = sources.filter((source) => !source.instruments.includes(instrument));

  if (isManual || query.isError) {
    return (
      <div className="flex flex-col gap-2 sm:col-span-2">
        <div className="grid gap-4 sm:grid-cols-2">
          <TextField description={description} label={`${label} provider`} onChange={(next) => onChange({ provider: next, symbol })} placeholder="HYPERLIQUID" value={provider} />
          <TextField description={description} label={`${label} symbol`} onChange={(next) => onChange({ provider, symbol: next })} placeholder="BTC" value={symbol} />
        </div>
        <FieldError message={error} />
        {query.isError ? (
          <p className="text-xs text-amber-600 dark:text-amber-500">Recent index feeds could not be loaded, so type the provider and symbol.</p>
        ) : (
          <Button className="h-auto w-fit p-0 text-xs text-muted-foreground hover:text-foreground" onClick={() => setIsManual(false)} type="button" variant="link">
            Pick from recent feeds
          </Button>
        )}
      </div>
    );
  }

  const describe = (source: { provider: string; symbol: string }) => `${source.provider} · ${source.symbol}`;
  return (
    <div className="flex min-w-0 flex-col gap-1.5 sm:col-span-2">
      <ContextTooltip description={description} htmlFor={pickerId}>
        {label}
      </ContextTooltip>
      <Select
        onValueChange={(value) => {
          if (value === ENTER_MANUALLY) return setIsManual(true);
          const [nextProvider = '', nextSymbol = ''] = value.split('\u0000');
          onChange({ provider: nextProvider, symbol: nextSymbol });
        }}
        value={current}
      >
        <SelectTrigger aria-invalid={Boolean(error)} id={pickerId}>
          <SelectValue placeholder={query.isPending ? 'Loading recent feeds…' : 'Pick a feed'} />
        </SelectTrigger>
        <SelectContent>
          {current && !isListed ? (
            <SelectGroup>
              <SelectLabel>Saved, not quoted in the last {query.data?.hours ?? 24} hours</SelectLabel>
              <SelectItem value={current}>{describe({ provider, symbol })}</SelectItem>
            </SelectGroup>
          ) : null}
          {ownFeeds.length ? (
            <SelectGroup>
              <SelectLabel>Prices {instrument}</SelectLabel>
              {ownFeeds.map((source) => (
                <SelectItem key={sourceKey(source.provider, source.symbol)} value={sourceKey(source.provider, source.symbol)}>
                  {describe(source)}
                </SelectItem>
              ))}
            </SelectGroup>
          ) : null}
          {otherFeeds.length ? (
            <SelectGroup>
              <SelectLabel>Other feeds</SelectLabel>
              {otherFeeds.map((source) => (
                <SelectItem key={sourceKey(source.provider, source.symbol)} value={sourceKey(source.provider, source.symbol)}>
                  {describe(source)}
                  <span className="text-muted-foreground"> · {source.instruments.join(', ')}</span>
                </SelectItem>
              ))}
            </SelectGroup>
          ) : null}
          <SelectSeparator />
          <SelectItem value={ENTER_MANUALLY}>Enter a provider and symbol…</SelectItem>
        </SelectContent>
      </Select>
      <FieldError message={error} />
    </div>
  );
};
IndexSourcePicker.displayName = 'IndexSourcePicker';
